<template>
  <div class="nav-menu-shell">
    <button
      type="button"
      class="nav-menu-command"
      aria-label="Open navigation menu"
      title="Menu"
      :aria-expanded="navigationOpen"
      @click="navigationOpen = !navigationOpen"
    >
      <span class="material-symbols-outlined" aria-hidden="true">menu</span>
    </button>

    <div v-if="navigationOpen" class="nav-menu-popover" role="menu" aria-label="Navigation menu">
      <button
        v-for="item in navigationItems"
        :key="item.path"
        type="button"
        class="nav-menu-item"
        role="menuitem"
        @click="navigateTo(item.path)"
      >
        <span class="material-symbols-outlined" aria-hidden="true">{{ item.icon }}</span>
        <span>{{ item.label }}</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import { isNavigationFailure, NavigationFailureType, useRouter } from "vue-router";
import store from "@/store/index.js";

const router = useRouter();
const navigationOpen = ref(false);

const navigationItems = computed(() => {
  const isLabAdmin = store.state.activeUser?.role === "lab-admin";
  const items = [
    { label: "Home", path: "/home", icon: "home" },
    { label: "My Profile", path: "/my-profile", icon: "person" },
    { label: "Manage Products", path: "/manage-products", icon: "inventory_2" },
  ];

  if (isLabAdmin) {
    items.push(
      { label: "Shared Products", path: "/shared-products", icon: "share" },
      { label: "Transport Racks", path: "/transport-racks", icon: "local_shipping" },
      { label: "Group Admin Page", path: "/group-admin-page", icon: "admin_panel_settings" },
      { label: "Project Manager", path: "/project-manager", icon: "assignment" },
      { label: "Analytics", path: "/analytics", icon: "monitoring" }
    );
  }

  items.push(
    { label: "Team Members", path: "/team-members", icon: "groups" },
    { label: "Grid Explorer", path: "/archives", icon: "grid_view" },
    { label: "Customer Support", path: "/customer-support", icon: "support_agent" }
  );

  if (isLabAdmin) {
    items.splice(items.length - 1, 0, {
      label: "Data Settings",
      path: "/data-settings",
      icon: "database",
    });
  }

  if (store.state.isMark) {
    items.splice(items.length - 1, 0, {
      label: "Administrative",
      path: "/admin",
      icon: "settings",
    });
  }

  return items;
});

function navigateTo(path) {
  navigationOpen.value = false;
  if (router.currentRoute.value.path === path) return;
  router
    .push(path)
    .then((failure) => {
      if (!failure) return;
      if (isNavigationFailure(failure, NavigationFailureType.aborted)) return;
      console.error("Navigation menu failed:", failure);
    })
    .catch((error) => {
      console.error("Navigation menu failed:", error);
    });
}
</script>

<style scoped>
.nav-menu-shell {
  position: relative;
  display: inline-flex;
  flex: 0 0 auto;
}

.nav-menu-command {
  display: none;
  align-items: center;
  justify-content: center;
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
  margin: 0;
  padding: 0;
  border: 1px solid #cbd7e6;
  border-radius: 6px;
  background: #f8fbff;
  color: #335c8b;
  cursor: pointer;
}

.nav-menu-command:hover,
.nav-menu-command:focus-visible {
  border-color: #8fb0d4;
  background: #eef6ff;
  outline: 0;
}

.nav-menu-command .material-symbols-outlined {
  font-size: 23px;
}

.nav-menu-popover {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 10000;
  display: grid;
  width: min(278px, calc(100vw - 24px));
  padding: 6px;
  border: 1px solid #cbd7e6;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 18px 36px rgb(15 23 42 / 0.22);
}

.nav-menu-item {
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  min-height: 40px;
  width: 100%;
  padding: 0 10px;
  border: 0;
  border-radius: 6px;
  background: #fff;
  color: #1f334c;
  font: inherit;
  font-size: 12px;
  font-weight: 900;
  text-align: left;
  cursor: pointer;
}

.nav-menu-item:hover,
.nav-menu-item:focus-visible {
  background: #eef6ff;
  color: #1d4f7a;
  outline: 0;
}

.nav-menu-item .material-symbols-outlined {
  color: #335c8b;
  font-size: 20px;
}

@media (max-width: 1279px) {
  .nav-menu-command {
    display: inline-flex;
  }
}
</style>
